import React, { useEffect, useRef } from 'react';
import styles from './inline-input-popover.module.css';
import { Flex, IconButton, icon as iconSize, Icon, Input } from '@nula/core-ui';
import { FloatingPanel } from '@/components/floating-panel/floating-panel';

export interface InlineInputPopoverProps {
  /** Viewport coords where the panel should anchor. */
  top: number;
  left: number;
  /** Current input value (controlled). */
  value: string;
  onChange: (next: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Floating label for the input. */
  label?: string;
  /** Optional leading icon name (Material Symbol). Omit for no icon. */
  leadingIcon?: string;
  /** Icon for the submit button (default: check). */
  submitIcon?: string;
  /** Accessible label for the submit button (default: "Submit"). */
  submitLabel?: string;
  /** Accessible label for the cancel/close button. If omitted, cancel button is hidden. */
  cancelLabel?: string;
  /** Input width — multiple of --space-7 (≈40px). Default: 4 (≈160px). */
  inputWidthInSpace7?: number;
  /** z-index override. */
  zIndex?: number;
  /** Ref on the outer FloatingPanel (for outside-click detection by the caller). */
  panelRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Shared floating input popover used across the editor:
 *   - Link create/edit (LinkEditPopover wraps this)
 *   - Comment input (CommentPlugin)
 *   - Image/YouTube URL input (UrlInputPlugin)
 *   - Any other "type a value, press enter, submit near selection" flow
 *
 * Design:
 *   - FloatingPanel wrapper positions at viewport coords
 *   - Optional leading icon (muted, no flex-shrink)
 *   - Controlled input, Enter submits, Escape cancels
 *   - Pill + transparent IconButton submit (Nula design language)
 *   - onMouseDown preventDefault on every element preserves the Lexical
 *     selection so commands dispatched on confirm have a valid target
 *
 * Intentionally controlled (value + onChange from caller) so callers own the
 * state and can react to it (e.g. disable submit when empty).
 */
export function InlineInputPopover({
  top,
  left,
  value,
  onChange,
  onConfirm,
  onCancel,
  label,
  leadingIcon,
  submitIcon = 'check',
  submitLabel = 'Submit',
  cancelLabel,
  inputWidthInSpace7 = 4,
  zIndex = 1100,
  panelRef: externalPanelRef,
}: InlineInputPopoverProps) {
  const localRef = useRef<HTMLDivElement>(null);
  const panelRef = externalPanelRef ?? localRef;

  // Close on outside click. Delayed (next tick) so the click that OPENED
  // the popover isn't immediately treated as "outside" and re-closing it.
  useEffect(() => {
    let active = false;
    const timer = setTimeout(() => { active = true; }, 200);
    const onDown = (e: MouseEvent) => {
      if (!active) return;
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onCancel, panelRef]);

  return (
    <FloatingPanel
      ref={panelRef}
      top={top}
      left={left}
      zIndex={zIndex}
      onMouseDown={(e) => e.preventDefault()}
      className={styles.panel}
    >
      <Flex align="center" gap="sm">
        {leadingIcon && (
          <Icon
            name={leadingIcon}
            size={iconSize.md}
            weight={300}
            aria-hidden="true"
            className={styles.leadingIcon}
          />
        )}
        <Input
          autoFocus
          size="sm"
          name="inline-input"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onConfirm();
            if (e.key === 'Escape') onCancel();
          }}
          label={label}
          className={styles.input}
          style={{ '--input-width-multiplier': inputWidthInSpace7 } as React.CSSProperties}
        />
        {cancelLabel && (
          <IconButton
            size="sm"
            pill
            transparent
            aria-label={cancelLabel}
            onMouseDown={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            <Icon name="close" size={iconSize.md} weight={300} />
          </IconButton>
        )}
        <IconButton
          size="sm"
          pill
          transparent
          aria-label={submitLabel}
          onMouseDown={(e) => {
            e.preventDefault();
            onConfirm();
          }}
        >
          <Icon name={submitIcon} size={iconSize.md} weight={300} />
        </IconButton>
      </Flex>
    </FloatingPanel>
  );
}
